/**
 * Data Operations - Sync-aware wrappers for database operations
 *
 * All write operations go through these helpers to ensure
 * sync status is properly tracked.
 */

import {
  getDb,
  generateId,
  type SyncStatus,
  type SyncableLogEntry,
  type SyncablePeriod,
  type SyncableUserSettings,
  type SyncableCgmReading,
  type SyncableDayNote,
} from "../db/user-database";
import { syncService } from "../services/sync";
import type {
  LogEntry,
  Period,
  UserSettings,
  CgmReading,
  DayNote,
} from "../types";

// --- Entry Operations ---

export async function saveEntry(
  entry: Omit<LogEntry, "id" | "createdAt" | "updatedAt"> & { id?: string },
): Promise<string> {
  const db = getDb();
  const now = new Date();
  const id = entry.id || generateId();

  // Preserve existing sync metadata if updating
  const existing = entry.id ? await db.entries.get(entry.id) : undefined;

  const syncableEntry: SyncableLogEntry = {
    ...entry,
    id,
    createdAt: existing?.createdAt || now,
    updatedAt: now,
    _syncStatus: "pending",
    _localVersion: Date.now(),
    _serverVersion: existing?._serverVersion,
  };

  await db.entries.put(syncableEntry);
  syncService.scheduleSyncSoon();

  return id;
}

export async function updateEntry(
  id: string,
  updates: Partial<LogEntry>,
): Promise<void> {
  const db = getDb();
  const existing = await db.entries.get(id);

  if (existing) {
    await db.entries.update(id, {
      ...updates,
      updatedAt: new Date(),
      _syncStatus: "pending" as SyncStatus,
      _localVersion: Date.now(),
      // _serverVersion preserved by update() - not replaced
    });
    syncService.scheduleSyncSoon();
  }
}

export async function deleteEntry(id: string): Promise<void> {
  const db = getDb();
  const existing = await db.entries.get(id);

  if (existing) {
    // Soft delete for sync
    await db.entries.update(id, {
      _syncStatus: "pending" as SyncStatus,
      _deletedAt: new Date().toISOString(),
      _localVersion: Date.now(),
    });
    syncService.scheduleSyncSoon();
  }
}

export async function getEntry(id: string): Promise<LogEntry | undefined> {
  const db = getDb();
  const entry = await db.entries.get(id);
  return entry && !entry._deletedAt ? entry : undefined;
}

export async function getAllEntries(): Promise<LogEntry[]> {
  const db = getDb();
  const entries = await db.entries.toArray();
  return entries.filter((e) => !e._deletedAt);
}

export async function getEntriesByPeriod(
  periodTag: string,
): Promise<LogEntry[]> {
  const db = getDb();
  const entries = await db.entries
    .where("periodTag")
    .equals(periodTag)
    .toArray();
  return entries.filter((e) => !e._deletedAt);
}

export async function getEntriesInDateRange(
  startDate: Date,
  endDate: Date,
): Promise<LogEntry[]> {
  const db = getDb();
  const entries = await db.entries
    .where("timestamp")
    .between(startDate, endDate, true, true)
    .toArray();
  return entries.filter((e) => !e._deletedAt);
}

// --- Period Operations ---

export async function savePeriod(
  period: Omit<Period, "id"> & { id?: string },
): Promise<string> {
  const db = getDb();
  const id = period.id || generateId();

  // Preserve existing sync metadata if updating
  const existing = period.id ? await db.periods.get(period.id) : undefined;

  const syncablePeriod: SyncablePeriod = {
    ...period,
    id,
    _syncStatus: "pending",
    _localVersion: Date.now(),
    _serverVersion: existing?._serverVersion,
  };

  await db.periods.put(syncablePeriod);
  syncService.scheduleSyncSoon();

  return id;
}

export async function getAllPeriods(): Promise<Period[]> {
  const db = getDb();
  const periods = await db.periods.orderBy("sortOrder").toArray();
  return periods.filter((p) => !p._deletedAt);
}

export async function getPeriod(id: string): Promise<Period | undefined> {
  const db = getDb();
  const period = await db.periods.get(id);
  return period && !period._deletedAt ? period : undefined;
}

// --- Settings Operations ---

export async function saveSettings(
  settings: Omit<UserSettings, "id">,
): Promise<void> {
  const db = getDb();

  // Preserve existing sync metadata (_serverVersion) from DB
  const existing = await db.settings.get("settings");

  const syncableSettings: SyncableUserSettings = {
    ...settings,
    id: "settings",
    _syncStatus: "pending",
    _localVersion: Date.now(),
    // Preserve _serverVersion from existing record
    _serverVersion: existing?._serverVersion,
  };

  await db.settings.put(syncableSettings);
  syncService.scheduleSyncSoon();
}

export async function getSettings(): Promise<UserSettings | undefined> {
  const db = getDb();
  return await db.settings.get("settings");
}

// --- CGM Reading Operations ---

export async function saveCgmReading(
  reading: Omit<CgmReading, "id"> & { id?: string },
): Promise<string> {
  const db = getDb();
  const id = reading.id || generateId();

  // Preserve existing sync metadata if updating
  const existing = reading.id
    ? await db.cgmReadings.get(reading.id)
    : undefined;

  const syncableReading: SyncableCgmReading = {
    ...reading,
    id,
    _syncStatus: "pending",
    _localVersion: Date.now(),
    _serverVersion: existing?._serverVersion,
  };

  await db.cgmReadings.put(syncableReading);
  syncService.scheduleSyncSoon();

  return id;
}

export async function saveCgmReadingsBatch(
  readings: Array<Omit<CgmReading, "id"> & { id?: string }>,
): Promise<string[]> {
  const db = getDb();
  const now = Date.now();

  // Get existing records to preserve _serverVersion
  const idsToCheck = readings.map((r) => r.id).filter(Boolean) as string[];
  const existingRecords =
    idsToCheck.length > 0 ? await db.cgmReadings.bulkGet(idsToCheck) : [];
  const existingMap = new Map(
    existingRecords
      .filter((r): r is SyncableCgmReading => r !== undefined)
      .map((r) => [r.id, r._serverVersion]),
  );

  const syncableReadings: SyncableCgmReading[] = readings.map((reading) => {
    const id = reading.id || generateId();
    return {
      ...reading,
      id,
      _syncStatus: "pending" as SyncStatus,
      _localVersion: now,
      _serverVersion: existingMap.get(id),
    };
  });

  await db.cgmReadings.bulkPut(syncableReadings);
  syncService.scheduleSyncSoon();

  return syncableReadings.map((r) => r.id);
}

export async function getCgmReadingsInDateRange(
  startDate: Date,
  endDate: Date,
): Promise<CgmReading[]> {
  const db = getDb();
  const readings = await db.cgmReadings
    .where("timestamp")
    .between(startDate, endDate, true, true)
    .toArray();
  return readings.filter((r) => !r._deletedAt);
}

export async function getLatestCgmReading(): Promise<CgmReading | undefined> {
  const db = getDb();
  const reading = await db.cgmReadings.orderBy("timestamp").last();
  return reading && !reading._deletedAt ? reading : undefined;
}

// --- Day Note Operations ---

export async function saveDayNote(
  note: Omit<DayNote, "id" | "createdAt" | "updatedAt"> & { id?: string },
): Promise<string> {
  const db = getDb();
  const now = new Date();
  const id = note.id || generateId();

  // Preserve existing sync metadata if updating
  const existing = note.id ? await db.dayNotes.get(note.id) : undefined;

  const syncableNote: SyncableDayNote = {
    ...note,
    id,
    createdAt: existing?.createdAt || now,
    updatedAt: now,
    _syncStatus: "pending",
    _localVersion: Date.now(),
    _serverVersion: existing?._serverVersion,
  };

  await db.dayNotes.put(syncableNote);
  syncService.scheduleSyncSoon();

  return id;
}

export async function updateDayNote(
  id: string,
  updates: Partial<DayNote>,
): Promise<void> {
  const db = getDb();
  const existing = await db.dayNotes.get(id);

  if (existing) {
    await db.dayNotes.update(id, {
      ...updates,
      updatedAt: new Date(),
      _syncStatus: "pending" as SyncStatus,
      _localVersion: Date.now(),
    });
    syncService.scheduleSyncSoon();
  }
}

export async function deleteDayNote(id: string): Promise<void> {
  const db = getDb();
  const existing = await db.dayNotes.get(id);

  if (existing) {
    // Soft delete for sync
    await db.dayNotes.update(id, {
      _syncStatus: "pending" as SyncStatus,
      _deletedAt: new Date().toISOString(),
      _localVersion: Date.now(),
    });
    syncService.scheduleSyncSoon();
  }
}

export async function getDayNote(id: string): Promise<DayNote | undefined> {
  const db = getDb();
  const note = await db.dayNotes.get(id);
  return note && !note._deletedAt ? note : undefined;
}

export async function getDayNotesForDate(date: string): Promise<DayNote[]> {
  const db = getDb();
  const notes = await db.dayNotes.where("date").equals(date).toArray();
  return notes.filter((n) => !n._deletedAt);
}

export async function getDayNotesInDateRange(
  startDate: string,
  endDate: string,
): Promise<DayNote[]> {
  const db = getDb();
  const notes = await db.dayNotes
    .where("date")
    .between(startDate, endDate, true, true)
    .toArray();
  return notes.filter((n) => !n._deletedAt);
}

export async function getAllDayNotes(): Promise<DayNote[]> {
  const db = getDb();
  const notes = await db.dayNotes.toArray();
  return notes.filter((n) => !n._deletedAt);
}

// --- Sync Status Queries ---

export async function getPendingChangesCount(): Promise<number> {
  const db = getDb();
  let count = 0;

  count += await db.entries.where("_syncStatus").equals("pending").count();
  count += await db.periods.where("_syncStatus").equals("pending").count();
  count += await db.settings.where("_syncStatus").equals("pending").count();
  count += await db.cgmReadings.where("_syncStatus").equals("pending").count();

  return count;
}

export async function getConflictsCount(): Promise<number> {
  const db = getDb();
  let count = 0;

  count += await db.entries.where("_syncStatus").equals("conflict").count();
  count += await db.periods.where("_syncStatus").equals("conflict").count();
  count += await db.settings.where("_syncStatus").equals("conflict").count();
  count += await db.cgmReadings.where("_syncStatus").equals("conflict").count();

  return count;
}

export async function resolveConflict(
  table: "entries" | "periods" | "settings" | "cgmReadings",
  id: string,
  keepLocal: boolean,
): Promise<void> {
  const db = getDb();

  let item: { _syncStatus?: string } | undefined;

  if (table === "entries") {
    item = await db.entries.get(id);
  } else if (table === "periods") {
    item = await db.periods.get(id);
  } else if (table === "settings") {
    item = await db.settings.get("settings");
  } else if (table === "cgmReadings") {
    item = await db.cgmReadings.get(id);
  }

  if (!item || item._syncStatus !== "conflict") return;

  const updateData = keepLocal
    ? {
        _syncStatus: "pending" as SyncStatus,
        _conflictData: undefined,
        _conflictAt: undefined,
        _localVersion: Date.now(),
      }
    : {
        _syncStatus: "synced" as SyncStatus,
        _conflictData: undefined,
        _conflictAt: undefined,
      };

  if (table === "entries") {
    await db.entries.update(id, updateData);
  } else if (table === "periods") {
    await db.periods.update(id, updateData);
  } else if (table === "settings") {
    await db.settings.update("settings", updateData);
  } else if (table === "cgmReadings") {
    await db.cgmReadings.update(id, updateData);
  }

  if (keepLocal) {
    syncService.scheduleSyncSoon();
  }
}
